
import Vue from 'vue';
import TagService from '@/services/TagService';
import UserService from '@/services/UserService';
import EntityMetaDialog from '@/components/misc/EntityMetaDialog.vue';
import { formatRuleActionLines, FormattedLine } from './ruleActionFormatter';
import {
  scopeEntityTypeOptions,
  scopeTriggerOptions,
  normalizeScopeFilters,
} from './ruleConstants';

type ScopeLine = { title: string; details: string };

export default Vue.extend({
  name: 'RuleInfoDialog',

  components: { EntityMetaDialog },

  props: {
    rule: { type: Object, required: true },
  },

  data: () => ({
    tags: [] as any[],
    users: [] as any[],
    localDialog: false,

    scopeEntityTypeOptions,
    scopeTriggerOptions,
  }),

  methods: {
    close() {
      this.localDialog = false;
    },

    dismiss() {
      this.close();
    },

    async loadOptions() {
      try {
        this.tags = await TagService.getAllCollectionPages(this.currentTenant);
        this.users = await UserService.getAllCollectionPages({
          tenant: this.currentTenant,
        });
      } catch (error) {
        console.error('Failed to load tags/users', error);
      }
    },

    getActionLineLabel(actionLineKey: string): string {
      if (actionLineKey === 'condition') {
        const translationKey = 'rule.condition.title';
        const translatedTitle = this.$t(translationKey) as string;
        return translatedTitle !== translationKey
          ? translatedTitle
          : 'Condition';
      }

      const actionTranslationKey = `rule.action.${actionLineKey}`;
      const translatedLabel = this.$t(actionTranslationKey) as string;
      if (translatedLabel !== actionTranslationKey) return translatedLabel;

      const simplifiedLineKey = actionLineKey.replace(/Id$/, '');
      const fallbackTranslationKey = `rule.action.${simplifiedLineKey}`;
      const fallbackTranslatedLabel = this.$t(fallbackTranslationKey) as string;
      if (fallbackTranslatedLabel !== fallbackTranslationKey)
        return fallbackTranslatedLabel;

      return simplifiedLineKey;
    },

    getActionTitle(actionType: string): string {
      const actionTitleMap: Record<string, string> = {
        'add-alert': this.$t('rule.action.addAlert') as string,
        'add-tag': this.$t('rule.action.addTag') as string,
        'add-permission': this.$t('rule.action.addPermission') as string,
      };

      return actionTitleMap[actionType] ?? actionType;
    },

    isTagLikeArray(items: any[]): boolean {
      if (!Array.isArray(items) || items.length === 0) return false;

      const firstItemObject = items.find(
        (item) => item && typeof item === 'object'
      );
      if (!firstItemObject) return false;

      return (
        'tag' in firstItemObject ||
        'label' in firstItemObject ||
        'name' in firstItemObject ||
        'title' in firstItemObject ||
        'displayName' in firstItemObject
      );
    },

    formatTagLikeArray(items: any[]): string {
      return items
        .map((item) => {
          if (!item || typeof item !== 'object') return '';

          return (
            (typeof (item as any).tag === 'string' && (item as any).tag) ||
            (typeof (item as any).label === 'string' && (item as any).label) ||
            (typeof (item as any).name === 'string' && (item as any).name) ||
            (typeof (item as any).title === 'string' && (item as any).title) ||
            (typeof (item as any).displayName === 'string' &&
              (item as any).displayName) ||
            ''
          );
        })
        .filter(Boolean)
        .join(', ');
    },

    formatValue(inputValue: any): string {
      if (inputValue === null || inputValue === undefined) return '';
      if (typeof inputValue === 'string') return inputValue;
      if (typeof inputValue === 'number' || typeof inputValue === 'boolean')
        return String(inputValue);

      if (Array.isArray(inputValue)) {
        if (this.isTagLikeArray(inputValue)) {
          const formattedTagList = this.formatTagLikeArray(inputValue);
          if (formattedTagList) return formattedTagList;
        }
        return inputValue.map(this.formatValue).filter(Boolean).join(', ');
      }

      if (typeof inputValue === 'object') {
        const valueObject: any = inputValue;

        const readableLabel =
          (typeof valueObject.tag === 'string' && valueObject.tag) ||
          (typeof valueObject.label === 'string' && valueObject.label) ||
          (typeof valueObject.name === 'string' && valueObject.name) ||
          (typeof valueObject.title === 'string' && valueObject.title) ||
          (typeof valueObject.displayName === 'string' &&
            valueObject.displayName);

        if (readableLabel) return String(readableLabel);
        if ('uuid' in valueObject && typeof valueObject.uuid === 'string')
          return valueObject.uuid;
        if ('id' in valueObject) return String(valueObject.id);

        return Object.values(valueObject)
          .map(this.formatValue)
          .filter(Boolean)
          .join(', ');
      }

      return String(inputValue);
    },

    summarizeBlock(blockLabel: string, blockObject: any): ScopeLine | null {
      if (!blockObject || typeof blockObject !== 'object') return null;

      const nonEmptyEntries = Object.entries(blockObject)
        .filter(([, entryValue]) => {
          return (
            entryValue !== undefined &&
            entryValue !== null &&
            !(typeof entryValue === 'string' && entryValue.trim() === '')
          );
        })
        .map(([entryKey, entryValue]) => {
          if (
            entryKey === 'tagIds' &&
            Array.isArray(entryValue) &&
            entryValue.length > 0
          ) {
            const resolvedTagNames = entryValue
              .map((tagUuid: any) => {
                const foundTag = this.tags.find(
                  (tag: any) => tag.uuid === tagUuid
                );
                return foundTag ? foundTag.name : String(tagUuid);
              })
              .filter(Boolean)
              .join(', ');

            const tagLabelTranslation =
              (this.$t('rule.summary.tag') as string) || 'Tag';

            return resolvedTagNames
              ? `${tagLabelTranslation}: ${resolvedTagNames}`
              : '';
          }

          const formattedValue = this.formatValue(entryValue);
          return formattedValue ? `${entryKey}: ${formattedValue}` : '';
        })
        .filter(Boolean);

      if (!nonEmptyEntries.length) return null;

      return { title: blockLabel, details: nonEmptyEntries.join(' · ') };
    },

    buildScopeSummary(scopeFilterGroup: any): ScopeLine[] {
      const summaryLines: ScopeLine[] = [];
      const scopeFilterSet = scopeFilterGroup || {};

      const deviceSummary = this.summarizeBlock(
        this.$t('entity.device') as string,
        scopeFilterSet.device
      );
      if (deviceSummary) summaryLines.push(deviceSummary);

      const propertySummary = this.summarizeBlock(
        this.$t('entity.property') as string,
        scopeFilterSet.property
      );
      if (propertySummary) summaryLines.push(propertySummary);

      const connectorSummary = this.summarizeBlock(
        this.$t('entity.connector') as string,
        scopeFilterSet.connector
      );
      if (connectorSummary) summaryLines.push(connectorSummary);

      const viewSummary = this.summarizeBlock(
        this.$t('entity.view') as string,
        scopeFilterSet.view
      );
      if (viewSummary) summaryLines.push(viewSummary);

      if (scopeFilterSet.reading) {
        const readingSummary = this.summarizeBlock(
          this.$t('entity.reading') as string,
          scopeFilterSet.reading
        );
        if (readingSummary) summaryLines.push(readingSummary);
      }

      if (scopeFilterSet.regex) {
        const regexAttribute = (scopeFilterSet.regex.attribute || '')
          .toString()
          .trim();
        const regexPattern = (scopeFilterSet.regex.regex || '')
          .toString()
          .trim();

        if (regexAttribute || regexPattern) {
          summaryLines.push({
            title: this.$t('rule.condition.regexShort') as string,
            details: [regexAttribute, regexPattern].filter(Boolean).join(' ~ '),
          });
        }
      }

      if (scopeFilterSet.dsl) {
        const dslExpression = (scopeFilterSet.dsl.dsl || '').toString().trim();
        const shortenedDslExpression =
          dslExpression.length > 120
            ? dslExpression.slice(0, 120) + '…'
            : dslExpression;

        if (shortenedDslExpression) {
          summaryLines.push({
            title: this.$t('rule.condition.dslShort') as string,
            details: shortenedDslExpression,
          });
        }
      }

      return summaryLines;
    },

    formatActionLines(actionItem: any): FormattedLine[] {
      return formatRuleActionLines(actionItem, {
        resolveTag: (tagUuid: string) => {
          const foundTag = this.tags.find((tag: any) => tag.uuid === tagUuid);
          return foundTag ? foundTag.name : tagUuid;
        },
        resolveUser: (userUuid: string) => {
          const foundUser = this.users.find(
            (user: any) => user.uuid === userUuid
          );
          return foundUser ? foundUser.email : userUuid;
        },
      });
    },
  },

  watch: {
    localDialog(isOpen: boolean) {
      if (isOpen) this.loadOptions();
    },
  },

  computed: {
    currentTenant(): string {
      return this.$root.$store.state.session.selectedTenant.uuid;
    },

    scopeSets(): any[] {
      return normalizeScopeFilters((this.rule as any).scopeFilters);
    },

    scopeSummary(): ScopeLine[][] {
      return this.scopeSets.map((scopeFilterGroup: any) =>
        this.buildScopeSummary(scopeFilterGroup)
      );
    },

    hasScopeFilters(): boolean {
      return this.scopeSummary.some((scopeLines) => scopeLines.length > 0);
    },

    formattedActions(): Array<{ action: string; lines: FormattedLine[] }> {
      const actions = ((this.rule as any)?.actions || []) as any[];
      return actions.map((actionItem: any) => ({
        action: String(actionItem.action || ''),
        lines: this.formatActionLines(actionItem),
      }));
    },
  },
});
